import {
	pxItemTypeGuards,
	DocumentRevision,
	ShareBaseItem,
	ShareBaseDocument,
	ShareBaseFolder
} from 'phoenix/domain';
import { AuthService } from 'phoenix/services';
import { OneOrMany } from 'util/domain';
export const template = require('./deleteDialog.template');

export class DeleteDialogController {
	public static $inject: string[] = [
		'$mdDialog',
		'pxAuthService',
		'gettextCatalog'
	];

	public readonly item: ShareBaseItem | ShareBaseItem[] | DocumentRevision;
	public readonly title: string;
	public readonly label: string;
	public readonly otherText: string;
	public readonly isRevision: boolean;
	public permanentlyDelete: boolean;

	constructor(
		private readonly $mdDialog: any,
		private readonly pxAuthService: AuthService,
		private readonly gettextCatalog: any
		) {
			this.permanentlyDelete = false;
			if (pxItemTypeGuards.isDocumentRevision(this.item as any)) {
				this.title = this.gettextCatalog.getString('Delete Revision');
				this.label = this.gettextCatalog.getString('Are you sure you want to permanently delete this revision?');
				this.permanentlyDelete = true;
			} else {
				if (!Array.isArray(this.item) || this.item.length === 1) {
					this.title = this.getDeleteTitleForSingleItem(this.unwrapFirstElement(this.item));
					this.label = this.getDeleteLabelForSingleItem(this.unwrapFirstElement(this.item));
					this.otherText = this.getOtherTextForSingleItem(this.unwrapFirstElement(this.item));
				} else{
					this.title = this.getDeleteTitleForMultiSelect(this.item);
					this.label = this.getDeleteLabelForMultiSelect(this.item);
					this.otherText = this.getOtherTextForMultiSelect(this.item);
				}
			}
	}

	public cancel() {
		this.$mdDialog.cancel({ cancelled: true });
	}

	public delete() {
		this.$mdDialog.hide(this.permanentlyDelete);
	}

	private getDeleteTitleForMultiSelect(items: ShareBaseItem[]): string {
		let title:string = '';
		let hasFolders = items.some(pxItemTypeGuards.isShareBaseFolder);
		let hasDocuments = items.some(pxItemTypeGuards.isShareBaseDocument);
		if (this.hasActiveTasks(items)) {
			if (hasFolders && hasDocuments) {
				title = this.gettextCatalog.getString('Delete Items With Tasks');
			} else if (hasFolders) {
				title = this.gettextCatalog.getString('Delete Folders With Tasks');
			} else {
				title = this.gettextCatalog.getString('Delete Documents With Tasks');
			}
		} else {
			if (hasFolders && hasDocuments) {
				title = this.gettextCatalog.getString('Delete Items');
			} else if (hasFolders) {
				title = this.gettextCatalog.getString('Delete Folders');
			} else {
				title = this.gettextCatalog.getString('Delete Documents');
			}
		}
		return title;
	}

	private getDeleteTitleForSingleItem(item: ShareBaseItem): string {
		let title:string = '';
		if (this.hasActiveTasks(item)) {
			if (pxItemTypeGuards.isShareBaseDocument(item)) {
				title = this.gettextCatalog.getString('Delete Document With Tasks');
			} else {
				title = this.gettextCatalog.getString('Delete Folder With Tasks');
			}
		} else {
			if (pxItemTypeGuards.isShareBaseDocument(item)) {
				title = this.gettextCatalog.getString('Delete Document');
			} else {
				title = this.gettextCatalog.getString('Delete Folder');
			}
		}

		return title;
	}

	private getDeleteLabelForMultiSelect(items: ShareBaseItem[]): string {
		let label:string = '';
		if (items.length === 1) {
			return this.getDeleteLabelForSingleItem(items[0]);
		} else {
			let containsShares:boolean = this.hasSharedContent(items);
			let hasTasks:boolean = this.hasActiveTasks(items);
			let hasFolders = items.some(pxItemTypeGuards.isShareBaseFolder);
			let hasDocuments = items.some(pxItemTypeGuards.isShareBaseDocument);
			if (hasTasks) {
				if (hasFolders && hasDocuments) {
					label = this.gettextCatalog.getString('Items in this selection have active tasks. Deleting a document will also delete its tasks.');
				} else if (hasFolders) {
					label = this.gettextCatalog.getString('Items in this selection have active tasks. Deleting a document will also delete its tasks.');
				} else {
					label = this.gettextCatalog.getString('Items in this selection have active tasks. Deleting a document will also delete its tasks.');
				}
			} else if (containsShares) {
				if (hasFolders && hasDocuments) {
					label = this.gettextCatalog.getString('Items in this selection are shared. Are you sure you want to delete the selected items?');
				} else if (hasFolders) {
					label = this.gettextCatalog.getString('Items in this selection are shared. Are you sure you want to delete the selected folders?');
				} else {
					label = this.gettextCatalog.getString('Items in this selection are shared. Are you sure you want to delete the selected documents?');
				}
			} else {
				if (hasFolders && hasDocuments) {
					label = this.gettextCatalog.getString('Are you sure you want to delete the selected items?');
				} else if (hasFolders) {
					label = this.gettextCatalog.getString('Are you sure you want to delete the selected folders?');
				} else {
					label = this.gettextCatalog.getString('Are you sure you want to delete the selected documents?');
				}
			}
			return label;			
		}
	}

	private getDeleteLabelForSingleItem(item: ShareBaseItem): string {
		let label:string = '';
		let hasTasks = this.hasActiveTasks(item);
		let isShare = this.hasSharedContent(item);
		if (pxItemTypeGuards.isShareBaseDocument(item)) {
			if (hasTasks) {
				label = this.gettextCatalog.getString('{{documentName}} has active tasks assigned to it. Deleting the document will also delete its tasks.', { documentName: item.DocumentName });
			} else if (isShare) {
				label = this.gettextCatalog.getString('{{documentName}} is shared. Are you sure you want to delete it?', { documentName: item.DocumentName });
			} else {
				label = this.gettextCatalog.getString('Are you sure you want to delete {{documentName}}?', { documentName: item.DocumentName });
			}
		} else if (pxItemTypeGuards.isShareBaseFolder(item)) {
			if (hasTasks) {
				label = this.gettextCatalog.getString('{{folderName}} contains items with active tasks. Deleting a document will also delete its tasks.', { folderName : item.FolderName });
			} else if (isShare) {
				label = this.gettextCatalog.getString('{{folderName}} is shared. Are you sure you want to delete it?', { folderName : item.FolderName });
			} else {
				label = this.gettextCatalog.getString('Are you sure you want to delete {{folderName}}?', { folderName: item.FolderName });
			}
		}
		return label;
	}

	private getOtherTextForSingleItem(item: ShareBaseItem): string {
		if (!this.hasActiveTasks(item)) {
			return '';
		}

		let otherText = '';
		if (pxItemTypeGuards.isShareBaseDocument(item)) {
			otherText = this.gettextCatalog.getString('Are you sure you want to delete both?');
		} else if (pxItemTypeGuards.isShareBaseFolder(item)) {
			otherText = this.gettextCatalog.getString('Are you sure you want to delete this folder?')
		}

		return otherText;
	}

	private getOtherTextForMultiSelect(items: ShareBaseItem[]): string {
		let otherText = ''
		if (this.hasActiveTasks(items)) {
			otherText = this.gettextCatalog.getString('Are you sure you want to proceed?')
		}

		return otherText;
	}

	private hasSharedContent(items: ShareBaseItem | ShareBaseItem[]): boolean {
		let hasShares:boolean = false;
		if (!Array.isArray(items)) {
			items = [items];
		}

		for (let i = 0; i < items.length; i++) {
			let item = items[i];
			if (pxItemTypeGuards.isShareBaseDocument(item)) {
				if (item.Owner !== this.pxAuthService.displayName) {
					hasShares = true;
					break;
				}
			} else if (pxItemTypeGuards.isShareBaseFolder(item)) {
				if (item.Owner !== this.pxAuthService.displayName || item.IsShare || item.IsSharedByCurrentUser) {
					hasShares = true;
					break;
				}
			}
		}
		return hasShares;
	}

	private hasActiveTasks(items: OneOrMany<ShareBaseItem>):boolean {
		if (!Array.isArray(items)) {
			items = [items];
		}
		return items.some(item => pxItemTypeGuards.isShareBaseDocument(item) ? item.HasActiveTasks : (<ShareBaseFolder>item).HasActiveDocumentTasks);
	}
	
	private unwrapFirstElement(toUnwrap) {
		if (Array.isArray(toUnwrap)) {
			return toUnwrap.length ? toUnwrap[0] : null;
		} else {
			return toUnwrap;
		}	 
	}
}


// WEBPACK FOOTER //
// ./src/sharebase/components/deleteDialog/deleteDialog.ts